import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Container,
  Alert,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { themeColors } from "../../utils/theme";
import { ButtonSkeleton } from "../common/SkeletonLoaders";

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [resetRequested, setResetRequested] = useState(false);
  const { resetPassword, confirmResetPassword } = useAuth();
  const navigate = useNavigate();

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    try {
      setMessage("");
      setError("");
      setLoading(true);
      
      // Add a minimum processing time to prevent timing attacks
      const MIN_PROCESSING_TIME = 500; // ms
      const startTime = Date.now();
      
      await resetPassword(email);
      
      // Ensure consistent timing regardless of account existence
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < MIN_PROCESSING_TIME) {
        await new Promise(resolve => setTimeout(resolve, MIN_PROCESSING_TIME - elapsedTime));
      }
      
      setResetRequested(true);
      setMessage("If an account with that email exists, a password reset link has been sent.");
    } catch (err) {
      console.error("Password reset error:", err);
      setError("An error occurred while processing your request. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code) {
      setError("Please enter the verification code");
      return;
    }

    if (!newPassword) {
      setError("Please enter a new password");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      setMessage("");
      setError("");
      setLoading(true);
      
      // Add a minimum processing time to prevent timing attacks
      const MIN_PROCESSING_TIME = 500; // ms
      const startTime = Date.now();
      
      await confirmResetPassword(email, code, newPassword);
      
      // Ensure consistent timing regardless of account existence
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < MIN_PROCESSING_TIME) {
        await new Promise(resolve => setTimeout(resolve, MIN_PROCESSING_TIME - elapsedTime));
      }
      
      setMessage("Password has been reset successfully");
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      console.error("Password reset confirmation error:", err);
      setError("An error occurred while confirming your password reset. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            {resetRequested ? "Enter Verification Code" : "Password Reset"}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {error}
            </Alert>
          )}

          {message && (
            <Alert severity="success" sx={{ width: "100%", mb: 2 }}>
              {message}
            </Alert>
          )}

          {!resetRequested ? (
            // Request Password Reset Form
            <Box
              component="form"
              onSubmit={handleRequestReset}
              sx={{ mt: 1, width: "100%" }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {loading ? (
                <ButtonSkeleton width="100%" height={48} />
              ) : (
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, bgcolor: themeColors.primary }}
                >
                  Request Reset
                </Button>
              )}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Link
                  to="/login"
                  style={{ textDecoration: "none", color: themeColors.primary }}
                >
                  Back to Login
                </Link>
                <Link
                  to="/register"
                  style={{ textDecoration: "none", color: themeColors.primary }}
                >
                  Need an account? Register
                </Link>
              </Box>
            </Box>
          ) : (
            // Confirm Password Reset Form
            <Box
              component="form"
              onSubmit={handleConfirmReset}
              sx={{ mt: 1, width: "100%" }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                value={email}
                disabled
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="code"
                label="Verification Code"
                name="code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="newPassword"
                label="New Password"
                type="password"
                id="newPassword"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm New Password"
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {loading ? (
                <ButtonSkeleton width="100%" height={48} />
              ) : (
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, bgcolor: themeColors.primary }}
                >
                  Reset Password
                </Button>
              )}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Link
                  to="/login"
                  style={{ textDecoration: "none", color: themeColors.primary }}
                >
                  Back to Login
                </Link>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
